<app-oee-modal
    [title]="title"
    [subtitle]="subtitle"
    icon="edit"
    [useMaterialIcon]="true"
    iconColor="blue"
    size="md"
    (closeClick)="onCancel()">

    <!-- Content -->
    <ng-container modal-content>
        <!-- Editable form -->
        <div class="edit-shift-form">
            <!-- Clock In -->
            <app-datetime-picker
                label="Clock In"
                mode="datetime"
                [required]="true"
                [(ngModel)]="clockInDateObj">
            </app-datetime-picker>

            <!-- Clock Out -->
            <app-datetime-picker
                label="Clock Out"
                mode="datetime"
                [hint]="isActive && !clockOutDateObj && !disableCompletionForActive ? 'Leave empty to clock out at current time' : ''"
                [disabled]="disableCompletionForActive"
                [referenceTime]="clockInDateObj"
                [(ngModel)]="clockOutDateObj">
            </app-datetime-picker>
            @if (endTimeBeforeStartTime) {
                <div class="field-error text-danger small mt-1">End time cannot be before start time</div>
            }

            <!-- Duration -->
            <div class="duration-display">
                <i class="fa-solid fa-stopwatch duration-icon"></i>
                <div class="duration-content">
                    <span class="duration-label">Duration:</span>
                    <span class="duration-value">{{ calculatedDuration }}</span>
                    @if (isMultiJob && allocations.length > 0) {
                        <span class="duration-note text-muted small ms-1">· split across {{ allocations.length }} jobs</span>
                    }
                </div>
            </div>



            @if (isMultiJob) {
                <!-- Multi-job allocations display -->
                <div class="multi-job-allocations mb-3">
                    <div class="field-label mb-1">
                        <i class="fa-solid fa-layer-group me-1 text-muted"></i>
                        Linked Jobs ({{ allocations.length }})
                    </div>
                    @if (allocationsLoading) {
                        <div class="text-muted small">Loading allocations...</div>
                    } @else {
                        <div class="allocation-list">
                            @for (alloc of allocations; track alloc.allocation_id) {
                                <div class="allocation-row">
                                    <a class="allocation-batch-number fw-medium text-primary" role="button" (click)="openJobDetails(alloc)">{{ alloc.batch_number }}</a>
                                </div>
                            }
                        </div>
                    }
                </div>
            } @else if (editMode === 'job') {
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Job</mat-label>
                    <mat-select [(ngModel)]="selectedBatchNumber"
                        panelClass="picker-search-panel"
                        (openedChange)="onJobPanelOpened($event)">
                        <div class="picker-search-box" (click)="$event.stopPropagation()">
                            <input matInput type="text" placeholder="Search jobs..."
                                [(ngModel)]="jobFilter"
                                [ngModelOptions]="{ standalone: true }"
                                (keydown)="$event.stopPropagation()"
                                autocomplete="off">
                        </div>
                        @if (filteredOpenBatches.length) {
                            <mat-optgroup label="Open">
                                @for (batch of filteredOpenBatches; track batch.batch_number) {
                                    <mat-option [value]="batch.batch_number">{{ batch.batch_number }}</mat-option>
                                }
                            </mat-optgroup>
                        }
                        @if (filteredCompletedBatches.length) {
                            <mat-optgroup label="Recently Completed">
                                @for (batch of filteredCompletedBatches; track batch.batch_number) {
                                    <mat-option [value]="batch.batch_number">
                                        {{ batch.batch_number }}
                                        @if (getRecentlyClosedPill(batch); as pill) {
                                            <span class="batch-recently-closed-pill"
                                                  [title]="pill.tooltip"
                                                  data-testid="edit-shift-recently-closed-pill">
                                                {{ pill.label }}
                                            </span>
                                        }
                                    </mat-option>
                                }
                            </mat-optgroup>
                        }
                        @if (filteredOpenBatches.length === 0 && filteredCompletedBatches.length === 0 && jobFilter) {
                            <mat-option disabled>No jobs match "{{ jobFilter }}"</mat-option>
                        }
                    </mat-select>
                </mat-form-field>

                @if (stationOptions.length > 1) {
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Station</mat-label>
                        <mat-select [ngModel]="selectedStationId"
                            (ngModelChange)="onStationSelected($event)"
                            panelClass="picker-search-panel"
                            (openedChange)="onStationPanelOpened($event)">
                            <div class="picker-search-box" (click)="$event.stopPropagation()">
                                <input matInput type="text" placeholder="Search stations..."
                                    [(ngModel)]="stationFilter"
                                    [ngModelOptions]="{ standalone: true }"
                                    (keydown)="$event.stopPropagation()"
                                    autocomplete="off">
                            </div>
                            @for (option of filteredStations; track option.station_id) {
                                <mat-option [value]="option.station_id">{{ option.station_name }}</mat-option>
                            }
                            @if (filteredStations.length === 0 && stationFilter) {
                                <mat-option disabled>No stations match "{{ stationFilter }}"</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                }
            }


            @if (editMode === 'activity') {
                <!-- Activity category badges -->
                <div class="activity-badges-row mb-2">
                    @for (cat of activityCategories; track cat.name) {
                        <button class="activity-badge-btn"
                            [class.active]="selectedActivityTag === cat.name"
                            (click)="selectCategory(cat.name)">
                            <i [class]="cat.icon"></i> {{ cat.name }}
                        </button>
                    }
                </div>

                @if (isWaitingOnPartsSelected) {
                    <div class="waiting-on-parts-picker mb-2">
                        <label class="field-label">Which job(s) are you waiting on parts for? <span class="text-muted">(optional)</span></label>

                        <select class="form-select form-select-sm mb-2" [(ngModel)]="waitingOnPartsStationId">
                            <option [ngValue]="null">All stations</option>
                            @for (station of waitingOnPartsStations; track station.id) {
                                <option [ngValue]="station.id">{{ station.station_code }} - {{ station.station_name }}</option>
                            }
                        </select>
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="Search by batch number, work order, equipment, or product"
                            [(ngModel)]="waitingOnPartsSearchQuery" />
                        <div class="batch-results batch-results-compact">
                            @for (batch of filteredWaitingOnPartsBatches; track batch.batch_event_id) {
                                <label class="batch-option"
                                    [class.selected]="isWaitingBatchSelected(batch)">
                                    <input
                                        type="checkbox"
                                        [checked]="isWaitingBatchSelected(batch)"
                                        (change)="toggleWaitingOnPartsBatch(batch)" />
                                    <div class="batch-copy">
                                        <div class="batch-title">{{ batch.batch_number }}</div>
                                        <div class="batch-subtitle">{{ getBatchSubtitle(batch) }}</div>
                                    </div>
                                </label>
                            } @empty {
                                <div class="empty-results">No jobs match this search.</div>
                            }
                        </div>

                        @if (waitingOnPartsBatches.length > 0) {
                            <div class="selected-waiting-batches mt-2">
                                @for (batch of waitingOnPartsBatches; track batch.batch_event_id) {
                                    <div class="selected-waiting-batch-row">
                                        <div class="selected-waiting-batch-copy">
                                            <span class="batch-title">{{ batch.batch_number }}</span>
                                            <span class="batch-subtitle">{{ getBatchSubtitle(batch) }}</span>
                                        </div>
                                        <button type="button" class="btn-remove-batch" (click)="toggleWaitingOnPartsBatch(batch)" title="Remove">
                                            <i class="fa-solid fa-xmark"></i>
                                        </button>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }


            }

            @if (showWorkTypeControl) {
                <div class="work-type-field" data-testid="edit-shift-work-type">
                    <span class="field-label">Work type</span>
                    <mat-radio-group
                        class="work-type-options"
                        aria-label="Work type"
                        [value]="classification"
                        (change)="classification = $event.value">
                        <mat-radio-button value="standard">Standard work</mat-radio-button>
                        <mat-radio-button value="rework">Rework</mat-radio-button>
                    </mat-radio-group>
                </div>
            }

            <!-- Notes (always visible regardless of mode) -->
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Notes</mat-label>
                <textarea matInput [(ngModel)]="notesValue"
                    placeholder="Add notes about this shift..."
                    rows="2"></textarea>
            </mat-form-field>

            <!-- Job completed checkbox (only when clocking out of a job-linked entry) -->
            @if (showJobCompletedCheckbox) {
                <div class="job-completed-row">
                    <mat-checkbox [(ngModel)]="jobCompleted" color="primary">
                        Job completed at this station
                    </mat-checkbox>
                    <div class="job-completed-hint">When checked, the job will be resolved or completed at this station on clock out.</div>
                </div>
            }
        </div>
    </ng-container>

    <!-- Actions -->
    <ng-container modal-actions>
        <!-- Delete (icon-only, pushed left) -->
        @if (!showDeleteConfirm) {
            <button mat-icon-button class="btn-delete me-auto" [matTooltip]="'Delete shift'"
                (click)="toggleDeleteConfirm()" [disabled]="saving || clockingOut">
                <mat-icon>delete</mat-icon>
            </button>
        } @else {
            <div class="delete-confirm me-auto">
                <span class="text-danger fw-semibold small">Delete?</span>
                <button mat-raised-button class="btn-danger btn-compact ms-1" (click)="onDelete()" [disabled]="deleting">
                    @if (deleting) { <mat-spinner diameter="18"></mat-spinner> } Yes
                </button>
                <button mat-button class="btn-compact ms-1" (click)="toggleDeleteConfirm()">No</button>
            </div>
        }

        <button mat-button (click)="onCancel()" [disabled]="saving || clockingOut">Cancel</button>

        @if (isActive) {
            <button mat-raised-button color="primary" class="btn-compact" (click)="onSave()" [disabled]="saving || clockingOut || !hasChanges || endTimeBeforeStartTime">
                @if (saving) { <mat-spinner diameter="18"></mat-spinner> }
                <mat-icon>save</mat-icon>
                Save
            </button>
            @if (!disableCompletionForActive) {
                <button mat-raised-button class="btn-danger btn-compact" (click)="onClockOut()" [disabled]="saving || clockingOut">
                    @if (clockingOut) { <mat-spinner diameter="18"></mat-spinner> }
                    <i class="fa-solid fa-right-from-bracket me-1"></i>
                    Clock Out
                </button>
            }
        } @else {
            <button mat-button type="button" (click)="onDuplicateEntry()" [disabled]="saving">
                <i class="fa-solid fa-play me-1"></i>
                Resume Entry
            </button>
            <button mat-raised-button color="primary" (click)="onSave()" [disabled]="saving || !hasChanges || endTimeBeforeStartTime">
                @if (saving) { <mat-spinner diameter="18"></mat-spinner> }
                <mat-icon>save</mat-icon>
                Save Changes
            </button>
        }
    </ng-container>

</app-oee-modal>
